import {
  FreshServiceProposal,
  ServiceProposalDependencies as ServiceProposalDependenciesData,
  SubmitFreshServiceProposal,
} from "@repo/common"
import {
  getDependencyItems,
  getFilteredDependencyItems,
  getViolatedDependencies,
} from "@repo/ui/lib/dependency-utils"
import { cn } from "@repo/ui/lib/utils"
import {
  SelectableCardDescription,
  SelectableCardLabel,
  useFieldContext,
} from "@repo/ui/tanstack-form"
import * as React from "react"
import { useTranslations } from "use-intl"
import { DependencyCard } from "./dependency-card"

interface ServiceProposalDependenciesProps {
  data: ServiceProposalDependenciesData
  className?: string
  isViolated?: boolean
  isVibrating?: boolean
  violatedDependencyIds?: Set<string>
  proposal?: FreshServiceProposal
  formValues?: SubmitFreshServiceProposal
}

export interface ServiceProposalDependenciesFieldProps {
  data: ServiceProposalDependenciesData
  proposal: FreshServiceProposal
  className?: string
}

export interface ServiceProposalDependenciesFieldRef {
  hasViolations: () => boolean
  handleSubmitAttempt: () => boolean
}

const ServiceProposalDependencies = React.forwardRef<
  HTMLDivElement,
  ServiceProposalDependenciesProps
>(
  (
    {
      className,
      data,
      isVibrating = false,
      violatedDependencyIds = new Set(),
      proposal,
      formValues,
    },
    ref,
  ) => {
    const t = useTranslations()
    const dependencyItems =
      proposal && formValues
        ? getFilteredDependencyItems(data, proposal, formValues, t)
        : getDependencyItems(data, t)

    if (dependencyItems.length === 0) return null

    return (
      <div ref={ref} className={cn("space-y-2", className)}>
        <SelectableCardLabel className="text-secondary-foreground">
          {t("ui.proposalForm.dependencies")}
        </SelectableCardLabel>
        <SelectableCardDescription>
          {t("ui.proposalForm.dependenciesDescription")}
        </SelectableCardDescription>

        {dependencyItems.map((dependency) => (
          <DependencyCard
            key={dependency.key}
            requirer={dependency.requirer}
            required={dependency.required}
            isViolated={violatedDependencyIds.has(dependency.key)}
            isVibrating={
              isVibrating && violatedDependencyIds.has(dependency.key)
            }
          />
        ))}
      </div>
    )
  },
)

ServiceProposalDependencies.displayName = "ServiceProposalDependencies"

export const ServiceProposalDependenciesField = React.forwardRef<
  ServiceProposalDependenciesFieldRef,
  ServiceProposalDependenciesFieldProps
>(({ data, proposal, className }, ref) => {
  const field = useFieldContext<SubmitFreshServiceProposal>()
  const [isVibrating, setIsVibrating] = React.useState(false)
  const [violatedDependencyIds, setViolatedDependencyIds] = React.useState<
    Set<string>
  >(new Set())

  const violatedIds = React.useMemo(
    () => getViolatedDependencies(proposal, field.form.state.values),
    [proposal, field.form.state.values],
  )
  const isViolated = violatedIds.size > 0

  React.useImperativeHandle(
    ref,
    () => ({
      hasViolations: () => violatedIds.size > 0,
      handleSubmitAttempt: () => {
        const hasViolations = violatedIds.size > 0
        if (hasViolations) {
          setViolatedDependencyIds(violatedIds)
          setIsVibrating(true)
          setTimeout(() => {
            setIsVibrating(false)
            setViolatedDependencyIds(new Set())
          }, 600)
        }
        return !hasViolations
      },
    }),
    [violatedIds],
  )

  return (
    <>
      <style>{`
        @keyframes violationVibrate {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        .violation-vibrate {
          animation: violationVibrate 0.6s ease-in-out;
        }
      `}</style>
      <ServiceProposalDependencies
        data={data}
        className={className}
        isViolated={isViolated}
        isVibrating={isVibrating}
        violatedDependencyIds={violatedDependencyIds}
        proposal={proposal}
        formValues={field.form.state.values}
      />
    </>
  )
})

ServiceProposalDependenciesField.displayName =
  "ServiceProposalDependenciesField"

export { ServiceProposalDependencies, type ServiceProposalDependenciesProps }
